import { z } from "zod"
import type { LeadStatus } from "@/shared/api/generated/types/marketing"
import {
	isLeadStatus,
	isLeadsPageSize,
	LEADS_DEFAULT_PAGE_SIZE,
	type LeadsPageSize,
} from "./constants"

export const isValidLeadDate = (value: string) => {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
	if (!match) return false

	const year = Number(match[1])
	const month = Number(match[2])
	const day = Number(match[3])
	const date = new Date(Date.UTC(year, month - 1, day))

	return (
		date.getUTCFullYear() === year &&
		date.getUTCMonth() === month - 1 &&
		date.getUTCDate() === day
	)
}

const optionalPositiveInteger = z.preprocess(
	(value) => (value === undefined ? undefined : Number(value)),
	z.number().int().min(1).optional(),
)

const optionalPageSize = z.preprocess(
	(value) => (value === undefined ? undefined : Number(value)),
	z.custom<LeadsPageSize>(isLeadsPageSize).optional(),
)

const optionalDate = z
	.string()
	.refine(isValidLeadDate)
	.optional()
	.catch(undefined)

export const leadsSearchSchema = z.object({
	search: z.string().optional().catch(undefined),
	status: z.custom<LeadStatus>(isLeadStatus).optional().catch(undefined),
	createdFrom: optionalDate,
	createdTo: optionalDate,
	page: optionalPositiveInteger.catch(undefined),
	pageSize: optionalPageSize.catch(undefined),
})

export type LeadsRouteSearch = z.infer<typeof leadsSearchSchema>

export type NormalizedLeadsSearch = {
	search: string
	status: LeadStatus | ""
	createdFrom: string
	createdTo: string
	page: number
	pageSize: LeadsPageSize
}

export const normalizeLeadsSearch = (
	search: LeadsRouteSearch,
): NormalizedLeadsSearch => ({
	search: search.search ?? "",
	status: search.status ?? "",
	createdFrom: search.createdFrom ?? "",
	createdTo: search.createdTo ?? "",
	page: search.page ?? 1,
	pageSize: search.pageSize ?? LEADS_DEFAULT_PAGE_SIZE,
})

export type UpdateLeadsRouteSearch = (patch: Partial<LeadsRouteSearch>) => void
